/**
 * The change page (/changes/:owner/:repo/:slug): the one page where the owner
 * reads a change's brief rendered — the decision, the contract, the behaviour,
 * the plan — and then its release, each with its state read from GitHub at sync
 * time (GET /changes/{owner}/{repo}/{slug}, ADR-218). The owner approves where
 * they always did, by merging: the page's only actions are its links.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Archive, Waypoints } from 'lucide-react';
import { getUserFriendlyErrorMessage } from '@puglieseweb/shared-observability';
import { Spinner } from '@systemdox/ui';
import { BreadcrumbBar } from '@/components/editor/BreadcrumbBar';
import { SizeBadge } from '@/components/changes/changeBadges';
import { StageStrip } from '@/components/changes/StageStrip';
import { CODE } from '@/components/changes/StageSection';
import { captureError } from '@/lib/sentry';
import { formatMoment, stageOf, stageStates, type Change } from '@/lib/changes/model';
import { changesApi } from '@/services/changesApi';
import { BriefSection } from './BriefSection';
import { ReleaseSection } from './ReleaseSection';

type Load = { status: 'loading' } | { status: 'missing' } | { status: 'failed'; message: string } | { status: 'ready'; change: Change };

/** A change the caller may not read answers as one that does not exist (SYD-K023). */
function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

export default function ChangePage() {
  const { owner = '', repo = '', slug = '' } = useParams();
  // Keep the query string on every in-SPA link — on localhost ?tenant IS the host selection.
  const { search } = useLocation();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    setLoad({ status: 'loading' });
    changesApi.getChange(owner, repo, slug).then(
      (change) => {
        if (live) setLoad({ status: 'ready', change });
      },
      (error) => {
        if (!live) return;
        if (isNotFound(error)) {
          setLoad({ status: 'missing' });
          return;
        }
        captureError(error, { component: 'ChangePage' });
        setLoad({ status: 'failed', message: getUserFriendlyErrorMessage(error) });
      },
    );
    return () => { live = false; };
  }, [owner, repo, slug]);

  const crumbs = [
    { label: 'Docs', to: { pathname: '/docs', search } },
    { label: 'Requirements', to: { pathname: '/docs/requirements', search } },
    { label: 'Changes', to: { pathname: '/changes', search } },
    { label: slug },
  ];

  if (load.status !== 'ready') {
    return (
      <div className="p-8">
        <BreadcrumbBar className="-mx-8 -mt-8 mb-6" crumbs={crumbs} />
        {load.status === 'loading' && (
          <div className="flex justify-center py-12">
            <Spinner aria-label="Loading the change" />
          </div>
        )}
        {load.status === 'failed' && (
          <p role="alert" data-change-failed className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
            {load.message}
          </p>
        )}
        {load.status === 'missing' && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900" data-change-missing>
            <Waypoints className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
            <h1 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No change at this address</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
              A change is the folder <code className={CODE}>specs/changes/{slug}/</code> in a connected repository you may read, here <code className={CODE}>{owner}/{repo}</code>.
            </p>
            <Link to={{ pathname: '/changes', search }} className="mt-4 inline-block text-sm font-medium text-violet-700 hover:underline dark:text-violet-300">
              Back to changes
            </Link>
          </div>
        )}
      </div>
    );
  }

  const { change } = load;
  const current = stageOf(change);

  return (
    <div className="p-8">
      <BreadcrumbBar className="-mx-8 -mt-8 mb-6" crumbs={crumbs} />

      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <code className={CODE}>{change.owner}/{change.repo} · {change.slug}</code>
          <SizeBadge size={change.size} />
        </div>
        <h1 className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">{change.title}</h1>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Read from GitHub {formatMoment(change.syncedAt)}.</p>
      </header>

      {change.archived && (
        <p data-archived className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
          <Archive className="h-4 w-4 shrink-0" />
          Done. This change is archived, and this page keeps its record, read-only.
        </p>
      )}

      <StageStrip states={stageStates(change)} current={current} className="mb-8" />

      <div className="space-y-6">
        <BriefSection change={change} current={current === 'shape' || current === 'build'} />
        <ReleaseSection change={change} current={current === 'release'} />
      </div>
    </div>
  );
}
